/**
 * Foto's verkleinen in de browser voordat ze verstuurd worden.
 *
 * Geheugen is op iOS Safari de echte grens: een tabblad dat te veel canvasgeheugen
 * vasthoudt (± 384 MB in totaal) wordt zonder melding herladen. Daarom:
 * - nooit groter decoderen dan nodig (`createImageBitmap` met resize, waar dat kan);
 * - beeldgrootte vooraf begrenzen (megapixels, niet alleen bestandsgrootte);
 * - `toBlob` (asynchroon, buiten de hoofdthread) in plaats van `toDataURL`;
 * - canvas en bitmap direct vrijgeven, ook bij een fout.
 */

/** Groter dan dit openen we niet: een telefoon kan erop vastlopen. */
export const MAX_IMAGE_BYTES = 25 * 1024 * 1024
/** Een 48 MP-camera levert ± 48 miljoen pixels; daarboven is het geen gewone foto meer. */
const MAX_SOURCE_PIXELS = 50_000_000

export const isImageFile = (file: File) => file.type.startsWith('image/') && file.size <= MAX_IMAGE_BYTES

/** Alleen deze links mogen als afbeeldingsbron: nooit `javascript:` of andere schema's. */
export const isSafeImageSrc = (src: string) => /^(https?:\/\/|data:image\/|blob:|\/)/i.test(src.trim())

/** Alleen de afmetingen lezen. Een <img> decodeert pas volledig als hij getekend wordt. */
function readSize(url: string): Promise<{ img: HTMLImageElement; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.decoding = 'async'
    img.onload = () => resolve({ img, width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => reject(new Error('Onleesbare afbeelding'))
    img.src = url
  })
}

/** Gedecodeerd op doelformaat waar de browser dat kan; anders de <img> zelf. */
async function scaledSource(file: File, img: HTMLImageElement, width: number, height: number) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { resizeWidth: width, resizeHeight: height, resizeQuality: 'high', imageOrientation: 'from-image' })
      return { source: bitmap as CanvasImageSource, release: () => bitmap.close() }
    } catch {
      /* Oudere Safari kent de resize-opties niet: val terug op de <img>. */
    }
  }
  return { source: img as CanvasImageSource, release: () => undefined }
}

type OutputType = 'image/jpeg' | 'image/png'

const toBlob = (canvas: HTMLCanvasElement, quality: number, type: OutputType) =>
  new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Foto verwerken lukte niet'))), type, quality),
  )

/** Een Blob als `data:`-URL: zelfstandig, dus ook na herladen nog geldig (in tegenstelling tot `blob:`). */
export const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('Foto lezen lukte niet'))
    reader.readAsDataURL(blob)
  })

/**
 * Verkleint tot maximaal `max` pixels aan de langste kant en geeft een JPEG als `Blob`
 * terug. Die gaat als bestand (multipart) naar de server: geen base64-overhead van ± 33%,
 * en de EXIF-gegevens (zoals locatie) zijn er door het hertekenen al af.
 */
export async function resizeImage(file: File, max = 1200, quality = 0.85, type: OutputType = 'image/jpeg'): Promise<Blob> {
  if (!isImageFile(file)) throw new Error('Geen foto of te groot bestand')
  const url = URL.createObjectURL(file)
  const canvas = document.createElement('canvas')
  let release = () => undefined as void
  try {
    const { img, width, height } = await readSize(url)
    if (!width || !height || width * height > MAX_SOURCE_PIXELS) throw new Error('Deze foto is te groot om te verwerken')
    const scale = Math.min(1, max / Math.max(width, height))
    canvas.width = Math.max(1, Math.round(width * scale))
    canvas.height = Math.max(1, Math.round(height * scale))
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Geen canvas')
    const scaled = await scaledSource(file, img, canvas.width, canvas.height)
    release = scaled.release
    ctx.drawImage(scaled.source, 0, 0, canvas.width, canvas.height)
    return await toBlob(canvas, quality, type)
  } finally {
    release()
    URL.revokeObjectURL(url)
    // Canvasgeheugen direct teruggeven: iOS wacht anders op de garbage collector.
    canvas.width = 0
    canvas.height = 0
  }
}
